import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Send,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Rocket,
  Clock,
  Globe,
} from 'lucide-react'
import { type AgentBuilderState } from '../AgentBuilder'

interface TestDeployStepProps {
  state: AgentBuilderState
  onDeploy: () => Promise<void>
  saving: boolean
}

interface TestResult {
  id: string
  input: string
  output: string
  status: 'success' | 'error'
  responseTime: number
  timestamp: Date
}

export function TestDeployStep({ state, onDeploy, saving }: TestDeployStepProps) {
  const [testInput, setTestInput] = useState('')
  const [testing, setTesting] = useState(false)
  const [testResults, setTestResults] = useState<TestResult[]>([])
  const [isPublic, setIsPublic] = useState(state.isPublic)

  const handleTest = async () => {
    if (!testInput.trim()) return

    setTesting(true)
    const startTime = Date.now()

    try {
      // Simulate API call - in production, this would call the test endpoint
      await new Promise((resolve) => setTimeout(resolve, 1500))

      const mockResponse = `Based on your configuration, here's a sample response:

This is ${state.name} responding in a ${state.personality.tone} tone.

I'm configured with the following capabilities:
${state.selectedSkills.map((s) => `- ${s.name}`).join('\n') || '- General assistance'}

How can I help you today?`

      const result: TestResult = {
        id: Date.now().toString(),
        input: testInput,
        output: mockResponse,
        status: 'success',
        responseTime: Date.now() - startTime,
        timestamp: new Date(),
      }

      setTestResults([result, ...testResults])
      setTestInput('')
    } catch (error) {
      const result: TestResult = {
        id: Date.now().toString(),
        input: testInput,
        output: 'Error: Failed to generate response. Please check your configuration.',
        status: 'error',
        responseTime: Date.now() - startTime,
        timestamp: new Date(),
      }
      setTestResults([result, ...testResults])
    } finally {
      setTesting(false)
    }
  }

  const successRate =
    testResults.length > 0
      ? Math.round(
          (testResults.filter((r) => r.status === 'success').length / testResults.length) * 100
        )
      : 0

  const avgResponseTime =
    testResults.length > 0
      ? Math.round(
          testResults.reduce((sum, r) => sum + r.responseTime, 0) / testResults.length
        )
      : 0

  const isReadyToDeploy =
    state.name &&
    state.systemPrompt &&
    state.primaryModel &&
    testResults.length > 0 &&
    successRate >= 80

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Test & Deploy</h2>
        <p className="text-gray-500 dark:text-gray-400">
          Test your agent's responses before deploying to production.
        </p>
      </div>

      {/* Agent Summary */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-200 dark:border-purple-800">
        <div className="flex items-start gap-4">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl ${
              state.color === 'purple'
                ? 'bg-purple-500'
                : state.color === 'blue'
                ? 'bg-blue-500'
                : state.color === 'emerald'
                ? 'bg-emerald-500'
                : state.color === 'amber'
                ? 'bg-amber-500'
                : state.color === 'rose'
                ? 'bg-rose-500'
                : 'bg-cyan-500'
            }`}
          >
            {state.emoji}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {state.name || 'Unnamed Agent'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">
              {state.description || 'No description'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{state.agentType}</Badge>
              <Badge variant="secondary">{state.primaryModel}</Badge>
              {state.selectedSkills.length > 0 && (
                <Badge variant="secondary">
                  {state.selectedSkills.length} skill{state.selectedSkills.length !== 1 && 's'}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Test Area */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white">Test Your Agent</h3>
          {testResults.length > 0 && (
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {successRate}% success rate
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4 text-blue-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  {avgResponseTime}ms avg
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Textarea
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            placeholder="Enter a test message to see how your agent responds..."
            className="rounded-xl resize-none"
            rows={3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.metaKey) {
                handleTest()
              }
            }}
          />
          <Button
            onClick={handleTest}
            disabled={testing || !testInput.trim()}
            className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white px-6"
          >
            {testing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        <p className="text-xs text-gray-500">Press Cmd+Enter to send</p>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-700 dark:text-gray-300">Test Results</h4>
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            {testResults.map((result) => (
              <div
                key={result.id}
                className={`rounded-xl border p-4 ${
                  result.status === 'success'
                    ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10'
                    : 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {result.status === 'success' ? (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {result.status === 'success' ? 'Success' : 'Failed'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{result.responseTime}ms</span>
                    <span>{result.timestamp.toLocaleTimeString()}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Input
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-lg p-2">
                      {result.input}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Output
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-lg p-2 whitespace-pre-wrap">
                      {result.output}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deployment Settings */}
      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white">Deployment Settings</h3>

        <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Make Public</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Allow anyone in your organization to use this agent
              </p>
            </div>
          </div>
          <Switch checked={isPublic} onCheckedChange={setIsPublic} />
        </div>
      </div>

      {/* Deployment Checklist */}
      <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Deployment Checklist</h4>
        <div className="space-y-3">
          <ChecklistItem
            label="Agent name configured"
            checked={!!state.name}
          />
          <ChecklistItem
            label="System prompt defined"
            checked={!!state.systemPrompt && state.systemPrompt.length >= 20}
          />
          <ChecklistItem
            label="Primary model selected"
            checked={!!state.primaryModel}
          />
          <ChecklistItem
            label="At least one test completed"
            checked={testResults.length > 0}
          />
          <ChecklistItem
            label="Test success rate above 80%"
            checked={successRate >= 80}
          />
        </div>
      </div>

      {/* Deploy Button */}
      <div className="flex items-center justify-between pt-4">
        {!isReadyToDeploy && (
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Complete all checklist items before deploying</span>
          </div>
        )}
        <div className="flex-1" />
        <Button
          onClick={onDeploy}
          disabled={saving || !isReadyToDeploy}
          className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Deploying...
            </>
          ) : (
            <>
              <Rocket className="mr-2 h-5 w-5" />
              Deploy Agent
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

function ChecklistItem({ label, checked }: { label: string; checked: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center ${
          checked
            ? 'bg-emerald-500 text-white'
            : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
        }`}
      >
        {checked ? (
          <CheckCircle className="h-3 w-3" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-current" />
        )}
      </div>
      <span
        className={`text-sm ${
          checked
            ? 'text-gray-900 dark:text-white'
            : 'text-gray-500 dark:text-gray-400'
        }`}
      >
        {label}
      </span>
    </div>
  )
}
